import { useQuery } from "@tanstack/react-query";
import {
  ColumnsMenu,
  DataTable,
  LinkButton,
  SegmentedFilter,
  Toolbar,
  ToolbarSpacer,
  copyText,
  errorText,
  useColumnVisibility,
  useNarrow,
  useToast,
  type ColumnVisibility,
} from "@amber/ui";
import type { InvitationRow, MembersResponse, SessionResponse } from "../../contracts/index";
import { membersQuery } from "../queries";
import type { MemberFilter, UsersSearch } from "../search";
import type { DialogState } from "../dialogs/AccessDialogs";
import { isMuted, memberColumns } from "./MemberRow";

const TOGGLES = [
  { id: "name", label: "Name", canHide: false },
  { id: "last_active", label: "Last active" },
  { id: "joined", label: "Joined via", hiddenByDefault: true },
];

function gridWidth(narrow: boolean, showJoined: boolean): number {
  if (narrow) return 640;
  return showJoined ? 1120 : 980;
}

function UsersToolbar({
  filter,
  counts,
  search,
  onSearch,
  visibility,
}: {
  filter: MemberFilter;
  counts: MembersResponse["counts"] | undefined;
  search: UsersSearch;
  onSearch: (next: UsersSearch) => void;
  visibility: ColumnVisibility;
}) {
  return (
    <Toolbar>
      <SegmentedFilter<MemberFilter>
        label="Status"
        value={filter}
        options={[
          { value: "all", label: "All", ...(counts ? { count: counts.all } : {}) },
          { value: "active", label: "Active", ...(counts ? { count: counts.active } : {}) },
          { value: "invited", label: "Invited", ...(counts ? { count: counts.invited } : {}) },
          { value: "deactivated", label: "Deactivated", ...(counts ? { count: counts.deactivated } : {}) },
        ]}
        onChange={(status) => {
          onSearch({ ...search, status: status === "all" ? undefined : status });
        }}
      />
      <span className="text-md text-muted max-compact:hidden">
        One role per person; each includes the ones before it: Viewer › Editor › Operator › Admin
      </span>
      <ToolbarSpacer />
      <ColumnsMenu columns={visibility.toggles} />
    </Toolbar>
  );
}

export function UsersTable({
  session,
  search,
  onSearch,
  onDialog,
}: {
  session: SessionResponse;
  search: UsersSearch;
  onSearch: (next: UsersSearch) => void;
  onDialog: (state: DialogState) => void;
}) {
  const members = useQuery(membersQuery);
  const toast = useToast();
  const visibility = useColumnVisibility(TOGGLES);
  const narrow = useNarrow();
  const filter = search.status ?? "all";
  const rows = (members.data?.rows ?? []).filter((r) => filter === "all" || r.status === filter);
  const counts = members.data?.counts;
  const noRows = filter === "invited" ? "No pending invitations" : "No users match this filter";
  const hidden = new Set([...visibility.hidden, ...(narrow ? ["email", "last_active"] : [])]);

  const copyLink = async (row: InvitationRow) => {
    const ok = await copyText(row.link);
    toast.show(
      ok ? `Invitation link for ${row.email} copied` : "Couldn't copy. Select the link in the invitation instead.",
    );
  };

  return (
    <>
      <UsersToolbar filter={filter} counts={counts} search={search} onSearch={onSearch} visibility={visibility} />
      <DataTable
        label="Users"
        columns={memberColumns({
          youId: session.user.id,
          onDialog,
          onCopy: (r) => void copyLink(r),
        })}
        rows={rows}
        rowId={(r) => r.id}
        minWidth={gridWidth(narrow, visibility.isVisible("joined"))}
        hidden={hidden}
        selectedId={search.selected ?? null}
        rowOptions={(r) => ({ muted: isMuted(r) })}
        isPending={members.isPending}
        empty={
          members.isError ? (
            <>
              Couldn&apos;t load users. {errorText(members.error)}{" "}
              <LinkButton
                onClick={() => {
                  void members.refetch();
                }}
              >
                Retry
              </LinkButton>
            </>
          ) : (
            noRows
          )
        }
      />
    </>
  );
}
